import { useState } from "react";
import { Link } from "react-router-dom";
import { Volume2, Check, Sparkles, X } from "lucide-react";
import { Button } from "../ui/button";

// Guest Demo — a one-tap sample round visitors can play WITHOUT signing up.
// Math: 3 quick Socratic-style questions. Speech: 3 practice words spoken in
// Pip's ElevenLabs voice (public whitelisted /api/landing/demo-word audio).

const API = process.env.REACT_APP_BACKEND_URL;

const MATH_QUESTIONS = [
  { q: "Pip has 3 acorns and finds 4 more. How many now?", choices: [6, 7, 8], answer: 7,
    hint: "Try counting up from 3: four more hops — 4, 5, 6…?" },
  { q: "Which number is bigger: 12 or 21?", choices: [12, 21], answer: 21,
    hint: "Look at the tens place first — 1 ten or 2 tens?" },
  { q: "Pip shares 8 stickers equally with a friend. How many each?", choices: [2, 4, 6], answer: 4,
    hint: "Splitting into 2 equal groups — what plus itself makes 8?" },
];

const SPEECH_WORDS = [
  { word: "rocket", tip: "Rrr — tongue up, engine sound!" },
  { word: "sunshine", tip: "Sss — soft snake sound to start." },
  { word: "butterfly", tip: "Buh — pop your lips gently." },
];

const audioCache = {};
const playDemoWord = async (word, setBusy) => {
  try {
    setBusy(true);
    if (!audioCache[word]) {
      const res = await fetch(`${API}/api/landing/demo-word/${word}`);
      const data = await res.json();
      if (data.status !== "ok") return;
      audioCache[word] = new Audio(`data:${data.mime};base64,${data.data_b64}`);
    }
    audioCache[word].playbackRate = 0.85; // global gentle Pip pace
    audioCache[word].currentTime = 0;
    await audioCache[word].play();
  } catch { /* autoplay may be blocked before first tap — harmless */ }
  finally { setBusy(false); }
};

// Celebration cheer: Pip's ElevenLabs voice when available, otherwise an
// instant synth "ta-da" arpeggio so finishing always sounds joyful.
let cheerAudio = null;
const synthTada = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const c = new Ctx();
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      const t = c.currentTime + i * 0.12;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.2, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      o.connect(g);
      g.connect(c.destination);
      o.start(t);
      o.stop(t + 0.55);
    });
  } catch { /* silence is fine */ }
};
const playCheer = async () => {
  try {
    if (!cheerAudio) {
      const res = await fetch(`${API}/api/landing/pip-cheer`);
      const data = await res.json();
      if (data.status === "ok") {
        cheerAudio = new Audio(`data:${data.mime};base64,${data.data_b64}`);
        cheerAudio.playbackRate = 0.85; // global gentle Pip pace
      }
    }
    if (cheerAudio) {
      cheerAudio.currentTime = 0;
      await cheerAudio.play();
      return;
    }
  } catch { /* fall through to the synth */ }
  synthTada();
};

const CONFETTI_COLORS = ["#22d3ee", "#a78bfa", "#fbbf24", "#10B981", "#f87171", "#f472b6"];
const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl"
    aria-hidden="true" data-testid="guest-demo-confetti">
    {Array.from({ length: 36 }).map((_, i) => (
      <span key={i} style={{
        position: "absolute", top: -12,
        left: `${(i * 37) % 100}%`,
        width: 8, height: 12,
        background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        borderRadius: i % 3 === 0 ? "50%" : 2,
        animation: `confettiFall ${1.8 + (i % 5) * 0.35}s ease-in ${(i % 7) * 0.12}s both`,
      }} />
    ))}
  </div>
);

const MathRound = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const [hint, setHint] = useState(false);
  const [score, setScore] = useState(0);
  const item = MATH_QUESTIONS[step];
  const pick = (c) => {
    if (c === item.answer) {
      const s = score + (hint ? 1 : 2);
      setScore(s);
      setHint(false);
      if (step + 1 >= MATH_QUESTIONS.length) onDone(s);
      else setStep(step + 1);
    } else {
      setHint(true);
    }
  };
  return (
    <div className="space-y-4" data-testid="guest-demo-math-round">
      <p className="text-xs font-bold uppercase tracking-widest text-violet-300">
        Question {step + 1} of {MATH_QUESTIONS.length}
      </p>
      <p className="text-lg font-bold text-white">{item.q}</p>
      {hint && (
        <p className="rounded-xl bg-amber-400/10 border border-amber-400/30 px-3 py-2 text-sm text-amber-200"
          data-testid="guest-demo-math-hint">
          Pip's hint: {item.hint}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        {item.choices.map((c) => (
          <Button key={c} onClick={() => pick(c)} variant="ghost"
            className="rounded-full px-6 h-12 text-lg font-bold text-white border border-white/25 hover:bg-violet-500/20 hover:text-white"
            data-testid={`guest-demo-math-choice-${c}`}>
            {c}
          </Button>
        ))}
      </div>
    </div>
  );
};

const SpeechRound = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const item = SPEECH_WORDS[step];
  const next = () => {
    if (step + 1 >= SPEECH_WORDS.length) onDone(SPEECH_WORDS.length * 2);
    else setStep(step + 1);
  };
  return (
    <div className="space-y-4" data-testid="guest-demo-speech-round">
      <p className="text-xs font-bold uppercase tracking-widest text-cyan-300">
        Word {step + 1} of {SPEECH_WORDS.length}
      </p>
      <p className="text-3xl font-extrabold font-display text-white capitalize">{item.word}</p>
      <p className="text-sm text-slate-300">{item.tip}</p>
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => playDemoWord(item.word, setBusy)} disabled={busy}
          className="rounded-full px-6 h-12 bg-cyan-500 hover:bg-cyan-400 text-white font-bold"
          data-testid="guest-demo-hear-button">
          <Volume2 className="h-5 w-5 mr-2" aria-hidden="true" />
          Hear Pip say it
        </Button>
        <Button onClick={next} variant="ghost"
          className="rounded-full px-6 h-12 text-white border border-white/25 hover:bg-cyan-500/20 hover:text-white"
          data-testid="guest-demo-said-it-button">
          <Check className="h-5 w-5 mr-2" aria-hidden="true" />
          I said it out loud!
        </Button>
      </div>
    </div>
  );
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

// Lead capture: invite the visitor back after their sample round.
const LeadCapture = ({ mode, score }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | done | invalid | network
  const submit = async () => {
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setState("invalid");
      return;
    }
    setState("busy");
    try {
      const res = await fetch(`${API}/api/landing/demo-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value, mode, score }),
      });
      setState(res.ok ? "done" : "invalid");
    } catch {
      setState("network");
    }
  };
  if (state === "done") {
    return (
      <p className="rounded-xl bg-emerald-400/10 border border-emerald-400/30 px-3 py-2 text-sm text-emerald-200"
        data-testid="guest-demo-lead-thanks">
        Thanks! Pip will send a friendly invite with more practice rounds.
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="guest-demo-lead-capture">
      <p className="text-xs text-slate-400">
        Want Pip to save this score and invite you back with more practice rounds?
      </p>
      <div className="flex gap-2">
        <input type="email" value={email} placeholder="parent@email.com"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="flex-1 rounded-full bg-white/10 border border-white/20 px-4 h-11 text-sm text-white placeholder:text-slate-500 outline-none focus:border-cyan-400"
          data-testid="guest-demo-lead-email-input" />
        <Button onClick={submit} disabled={state === "busy"}
          className="rounded-full h-11 px-5 bg-cyan-500 hover:bg-cyan-400 text-white font-bold"
          data-testid="guest-demo-lead-submit-button">
          {state === "busy" ? "…" : "Send"}
        </Button>
      </div>
      {state === "invalid" && (
        <p className="text-xs text-rose-300" data-testid="guest-demo-lead-error">
          Please enter a valid email address.
        </p>
      )}
      {state === "network" && (
        <p className="text-xs text-amber-300" data-testid="guest-demo-lead-network-error">
          Connection hiccup — please try again in a moment.
        </p>
      )}
    </div>
  );
};

export const GuestDemo = ({ mode, onClose }) => {
  const [score, setScore] = useState(null);
  if (!mode) return null;
  const isMath = mode === "math";
  const celebrate = (s) => {
    setScore(s);
    playCheer();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog" aria-modal="true" data-testid="guest-demo-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-3xl border border-white/15 bg-[#0d1626] p-7 space-y-5 shadow-[0_0_60px_rgba(34,211,238,0.15)]">
        {score !== null && <Confetti />}
        <button onClick={onClose} aria-label="Close demo"
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
          data-testid="guest-demo-close-button">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
        <p className="inline-block rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-sky-200">
          Quick sample — no sign-up needed
        </p>
        <h2 className="text-base md:text-lg font-extrabold font-display text-white">
          {isMath ? "Pip's Math Mini-Round" : "Pip's Speech Mini-Round"}
        </h2>
        {score === null ? (
          isMath
            ? <MathRound onDone={celebrate} />
            : <SpeechRound onDone={celebrate} />
        ) : (
          <div className="space-y-4 text-center" data-testid="guest-demo-result">
            <Sparkles className="h-10 w-10 mx-auto text-amber-300" aria-hidden="true" />
            <p className="text-xl font-extrabold text-white">
              You earned {score} nectar drops!
            </p>
            <p className="text-sm text-slate-300">
              That's just a taste — Pip coaches whole adventures in speech and math,
              with progress your family can see.
            </p>
            <LeadCapture mode={mode} score={score} />
            <Button asChild size="lg"
              className="rounded-full px-8 h-12 bg-[#10B981] hover:bg-[#0EA371] text-white font-bold"
              data-testid="guest-demo-register-button">
              <Link to="/auth?mode=register">Get Started</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
